import { assertUnreachable } from '@sgme/fp';

import { SavedCashForward } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashForward.ts';
import { SavedCashNdf } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashNdf.ts';
import { SavedCashSpot } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashSpot.ts';
import { SavedFlexiForward } from '@/domain/WorkspaceForex/savedProducts/flexi-forward/SavedFlexiForward.ts';
import { SavedForwardAccumulator } from '@/domain/WorkspaceForex/savedProducts/forwardAccumulator/SavedForwardAccumulator.ts';
import { SavedOption } from '@/domain/WorkspaceForex/savedProducts/option/SavedOption.ts';
import { SavedCallOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedCallOrder.ts';
import { SavedFalconOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedFalconOrder.ts';
import { SavedFixingOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedFixingOrder.ts';
import { SavedNightjarOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedNightjarOrder.ts';
import { SavedStopLossOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedStopLossOrder.ts';
import { SavedTakeProfitOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedTakeProfitOrder.ts';
import { SavedTwapOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedTwapOrder.ts';
import { SavedSwapNds } from '@/domain/WorkspaceForex/savedProducts/swap/SavedSwapNds.ts';
import { SavedSwapRegular } from '@/domain/WorkspaceForex/savedProducts/swap/SavedSwapRegular.ts';
import { SavedTargetAccumulator } from '@/domain/WorkspaceForex/savedProducts/targetAccumulator/SavedTargetAccumulator.ts';
import type { AppState } from '@/store/store.ts';
import { selectActiveTab } from '@/store/tabs/slice.ts';
import type { Tab } from '@/store/tabs/types.ts';
import { selectTilesOfTab } from '@/store/tiles/selectors.ts';

export function selectFullTab(state: AppState) {
  const selectedTab = selectActiveTab(state);
  if (!selectedTab) {
    return undefined;
  }

  const tilesOfTab = selectTileIdsOfTab(state, selectedTab);

  return {
    ...selectedTab,
    tiles: selectTilesOfTab(state, selectedTab.id),
    products: {
      order: Object.entries(state.orders)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, order]) => {
          const { type } = order;

          switch (type) {
            case 'StopLossOrder':
              return SavedStopLossOrder.fromFxStopLossOrder(order, tileId);
            case 'TakeProfitOrder':
              return SavedTakeProfitOrder.fromFxTakeProfitOrder(order, tileId);
            case 'FixingOrder':
              return SavedFixingOrder.fromFxFixingOrder(order, tileId);
            case 'Twap+Order':
              return SavedTwapOrder.fromFxTwapOrder(order, tileId);
            case 'CallOrder':
              return SavedCallOrder.fromFxCallOrder(order, tileId);
            case 'FalconOrder':
              return SavedFalconOrder.fromFxFalconOrder(order, tileId);
            case 'NightjarOrder':
              return SavedNightjarOrder.fromFxNightjarOrder(order, tileId);
            default:
              return assertUnreachable(type, `Unexpected order type: ${type}`);
          }
        }),
      cash: Object.entries(state.cash)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, cash]) => {
          const { type } = cash;
          switch (type) {
            case 'FxFwd':
              return SavedCashForward.fromFxCashForward(cash, tileId);
            case 'FxNdf':
              return SavedCashNdf.fromFxCashNdf(cash, tileId);
            case 'FxSpot':
              return SavedCashSpot.fromFxCashSpot(cash, tileId);
            default:
              return assertUnreachable(type, `Unexpected cash type: ${type}`);
          }
        }),
      option: Object.entries(state.option)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, option]) => SavedOption.fromOption(option, tileId)),
      targetAccumulator: Object.entries(state.targetAccumulator)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, targetAccumulator]) => SavedTargetAccumulator.fromTargetAccumulator(targetAccumulator, tileId)),
      forwardAccumulator: Object.entries(state.forwardAccumulator)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, forwardAccumulator]) =>
          SavedForwardAccumulator.fromForwardAccumulator(forwardAccumulator, tileId),
        ),
      swap: Object.entries(state.swap)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, swap]) => {
          if (swap.isNonDeliverable) {
            return SavedSwapNds.fromFxSwapNds(swap, tileId);
          }
          return SavedSwapRegular.fromFxSwapRegular(swap, tileId);
        }),
      flexiForward: Object.entries(state.flexiForward)
        .filter(([tileId]) => tilesOfTab.has(tileId))
        .map(([tileId, flexi]) => SavedFlexiForward.fromFlexiForward(flexi, tileId)),
    },
  };
}

export function selectTileIdsOfTab(state: AppState, selectedTab: Tab) {
  return Object.values(state.tiles.entities).reduce((tileIds, tile) => {
    if (tile.tabId === selectedTab.id) {
      tileIds.add(tile.tileId);
    }
    return tileIds;
  }, new Set<Tab['id']>());
}
